import { portalPaths } from '@provision/portal-routes';
import { useState } from 'react';
import { isNullish } from 'remeda';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FloatingStripButton } from 'src/components/FloatingStrip/floating-strip';
import { ModalContainer, ModalList, type ModalListItem } from 'src/components/ModalContainer';
import {
  BulkUpdateEntitiesProgress,
  BulkUpdateError,
  isProgressTerminal,
} from 'src/graphql/entities/custom-view/custom-view-bulk-update';
import {
  BulkUpdateOperationKind,
  getBulkUpdateBannerTitle,
  getBulkUpdateProgressDetails,
} from './bulk-update-banner.helpers';
import {
  BANNER_ICON_SIZE,
  BulkJobProgress,
  BulkJobStatusIcon,
  BulkJobStrip,
  BulkJobText,
} from './bulk-update-job-banner.elements';

const MAX_VISIBLE_RECORDS = 50;

const SITE_ENTITY_NAME = 'Address';

interface ErrorRowItem extends ModalListItem {
  recordId?: string;
}

const getErrorRows = (errors: BulkUpdateError[]): ErrorRowItem[] =>
  errors.flatMap(({ message, records }, groupIndex) => {
    const failedRecords = records ?? [];
    // A group header only renders above rows, so a message with no records becomes its own row.
    if (failedRecords.length === 0) {
      return [{ id: `${groupIndex}:message`, title: message, disabled: true }];
    }
    const rows: ErrorRowItem[] = failedRecords.slice(0, MAX_VISIBLE_RECORDS).map((record) => ({
      id: `${groupIndex}:${record.id}`,
      group: message,
      title: record.label || record.id,
      recordId: record.id,
    }));
    if (failedRecords.length > MAX_VISIBLE_RECORDS) {
      rows.push({
        id: `${groupIndex}:more`,
        group: message,
        title: `...and ${failedRecords.length - MAX_VISIBLE_RECORDS} more`,
        disabled: true,
      });
    }
    return rows;
  });

interface BulkUpdateErrorsModalProps {
  open: boolean;
  progress?: BulkUpdateEntitiesProgress;
  partnerPrefix?: string;
  onClose: () => void;
}

const BulkUpdateErrorsModal = ({
  open,
  progress,
  partnerPrefix,
  onClose,
}: BulkUpdateErrorsModalProps) => {
  const isSiteList = progress?.entityName === SITE_ENTITY_NAME;

  const openSite = ({ recordId }: ErrorRowItem) => {
    if (recordId) {
      window.open(
        `${partnerPrefix ? `/${partnerPrefix}` : ''}${portalPaths.site.full(recordId)}`,
        '_blank',
        'noopener,noreferrer',
      );
    }
  };

  return (
    <ModalContainer
      open={open}
      size="medium"
      title="Bulk update errors"
      onClose={onClose}
      data-testid="bulk-update-errors-modal"
    >
      <ModalList
        items={getErrorRows(progress?.errors ?? [])}
        readOnly={!isSiteList}
        onSelect={openSite}
        emptyText="This operation reported failures, but no detailed error payload was returned."
        testId="bulk-update-errors-modal"
      />
    </ModalContainer>
  );
};

interface BulkUpdateJobBannerProps {
  progress?: BulkUpdateEntitiesProgress;
  isLoading: boolean;
  isUndoInProgress: boolean;
  operationKind?: BulkUpdateOperationKind;
  partnerPrefix?: string;
  onUndo: () => void;
  onClose: () => void;
}

export const BulkUpdateJobBanner = ({
  progress,
  isLoading,
  isUndoInProgress,
  operationKind = 'update',
  partnerPrefix,
  onUndo,
  onClose,
}: BulkUpdateJobBannerProps) => {
  const [isErrorsDialogOpen, setIsErrorsDialogOpen] = useState(false);
  const status = progress?.status?.toLowerCase();
  const isTerminal = isProgressTerminal(status);

  if (!isLoading && !progress) {
    return null;
  }
  // Real poll-driven progress — deliberately no wall-clock simulation, so a stalled job looks stalled.
  const canUndo = Boolean(
    progress &&
      (status === 'done' || status === 'partial_failure') &&
      !progress.undoUsedAt &&
      (!progress.undoIneligibilityCode || progress.undoIneligibilityCode === 'none'),
  );
  const progressPercent =
    progress && progress.total > 0
      ? Math.max(0, Math.min(100, Math.round((progress.processed / progress.total) * 100)))
      : 0;
  const title = getBulkUpdateBannerTitle({ operationKind, status, isUndoInProgress });
  const details = getBannerDetails({
    status,
    isUndoInProgress,
    progress,
    operationKind,
  });
  const hasErrors = Boolean(
    (progress?.failedCount ?? 0) > 0 || (progress?.errors?.length ?? 0) > 0,
  );

  const undoUnavailableNote =
    !isNullish(progress?.undoIneligibilityCode) && progress?.undoIneligibilityCode !== 'none'
      ? `Undo unavailable: ${progress?.undoIneligibilityMessage || progress?.undoIneligibilityCode}`
      : null;

  return (
    <>
      <BulkJobStrip tone="raised" data-testid="bulk-update-banner" data-status={status}>
        <BulkJobStatusIcon status={status} isUndoInProgress={isUndoInProgress} />
        <BulkJobText>
          <b>{title}</b>
          <span>{undoUnavailableNote ? `${details} • ${undoUnavailableNote}` : details}</span>
        </BulkJobText>

        {hasErrors ? (
          <FloatingStripButton
            onClick={() => setIsErrorsDialogOpen(true)}
            data-testid="bulk-update-errors-open"
          >
            View errors
          </FloatingStripButton>
        ) : null}
        {canUndo ? (
          <FloatingStripButton
            onClick={onUndo}
            disabled={isUndoInProgress}
            startIcon={<DynamicIcon name="undo-2" library="lucide" size={BANNER_ICON_SIZE} />}
            data-testid="bulk-update-undo"
          >
            {isUndoInProgress ? 'Undoing...' : 'Undo'}
          </FloatingStripButton>
        ) : null}
        <FloatingStripButton
          iconOnly
          onClick={onClose}
          aria-label="Dismiss bulk update banner"
          startIcon={<DynamicIcon name="x" library="lucide" size={BANNER_ICON_SIZE} />}
          data-testid="bulk-update-banner-close"
        />

        {!isTerminal || isLoading ? (
          <BulkJobProgress
            variant={progress?.total ? 'determinate' : 'indeterminate'}
            value={progress?.total ? progressPercent : undefined}
          />
        ) : null}
      </BulkJobStrip>

      <BulkUpdateErrorsModal
        open={isErrorsDialogOpen}
        progress={progress}
        partnerPrefix={partnerPrefix}
        onClose={() => setIsErrorsDialogOpen(false)}
      />
    </>
  );
};

function getBannerDetails(input: {
  status?: string;
  isUndoInProgress: boolean;
  progress?: BulkUpdateEntitiesProgress;
  operationKind: BulkUpdateOperationKind;
}): string {
  if (input.isUndoInProgress) {
    return 'Reverting previous changes...';
  }

  if (input.status?.toLowerCase() === 'undone') {
    const conflictSuffix = input.progress?.undoConflictCount
      ? ` • ${input.progress.undoConflictCount} conflict(s) skipped`
      : '';

    return `${Math.max(0, input.progress?.updatedCount ?? 0)} reverted${conflictSuffix}`;
  }

  return getBulkUpdateProgressDetails({
    operationKind: input.operationKind,
    progress: input.progress,
  });
}
